import ListLayout from "../../components/layout/main/ListLayout";

import {
    Background,
    DText,
    Flex,
    Padding,
    PageCard
} from "../../styles/CommonStyles";

import { fadeVariants } from "../../core/utils/theme";
import Text from "../../core/utils/Text";

import {
    ProfileInfoAnimate,
    ProfileInfoItem
} from "../../styles/main/profile";

import Input from "../../components/common/input/Input";
import CheckBox from "../../components/common/input/CheckBox";
import ActionButton from "../../components/common/buttons/ActionButton";
import Select from "../../components/common/dropdown/Select";

import { useState, useEffect, useMemo } from "react";

import {
    useCreateUser,
    useGetFiatCategories
} from "../../core/services/react-query/user";

import {
    inputLength,
    numberInputTypes
} from "../../core/utils/validation";

import countries from "i18n-iso-countries";
import en from "i18n-iso-countries/langs/en.json";
import {
    getCountryCallingCode,
    isSupportedCountry,
    parsePhoneNumberFromString
} from "libphonenumber-js/max";

const profileInfoInitial = {
    email: "",
    password: "",
    firstName: "",
    mobile: "",
    lastName: "",
    isBusiness: false,
    countryCode: "",
    businessName: "",
    businessCategory: ""
};

countries.registerLocale(en);

const AddUser = () => {
    const {
        mutate: createUser,
        isSuccess,
        isLoading
    } = useCreateUser();

    const {
        data: fiatCategories,
        isLoading: isFiatCategoriesLoading
    } = useGetFiatCategories();

    const [profileInfo, setProfileInfo] = useState(
        profileInfoInitial
    );
    const [errors, setErrors] = useState({});

    const [showPassword, setShowPassword] = useState(false);

    const isBusiness = profileInfo.isBusiness;

    const mobilePrefix = useMemo(() => {
        if (
            !profileInfo.countryCode ||
            !isSupportedCountry(profileInfo.countryCode)
        ) {
            return null;
        }

        return `+${getCountryCallingCode(
            profileInfo.countryCode
        )}`;
    }, [profileInfo.countryCode]);
    const countryOptions = useMemo(() => {
        return Object.entries(
            countries.getNames("en", {
                select: "official"
            })
        ).map(([code, name]) => name);
    }, []);

    const businessCategories = useMemo(() => {
        if (isFiatCategoriesLoading) {
            return [];
        }

        return (
            fiatCategories
                ?.filter(
                    (item) =>
                        item.active &&
                        item.accountType === "business"
                )
                .map((item) => item.code) || []
        );
    }, [
        fiatCategories,
        isFiatCategoriesLoading
    ]);

    const onInputValueChange = (key, value) => {
        const normalizedValue =
            key === "mobile"
                ? value.replace(/\D/g, "")
                : value;

        setProfileInfo((state) => ({
            ...state,
            [key]: normalizedValue
        }));

        // Clear field error as soon as user starts fixing it
        if (errors[key]) {
            setErrors((state) => ({
                ...state,
                [key]: ""
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        const email = profileInfo.email.trim();
        const mobile = profileInfo.mobile.trim();
        const password = profileInfo.password;

        // Email
        if (!email) {
            newErrors.email = "Email is required";
        }

        // Password
        if (!password) {
            newErrors.password = "Password is required";
        } else if (password.length < 8) {
            newErrors.password =
                "Password must be at least 8 characters";
        }

        setErrors(newErrors);

        return Object.keys(newErrors).length === 0;
    };


    const getNewProfilePayload = () => {
        const payload = {
            email: profileInfo.email,
            password: btoa(profileInfo.password),
            countryCode: countries.getAlpha2Code(profileInfo.countryCode, "en"),
            isBusiness: profileInfo.isBusiness
        };

        if (profileInfo.mobile) {
            payload.mobile = profileInfo.mobile;
        }

        if (profileInfo.isBusiness) {
            payload.businessName = profileInfo.businessName;
            payload.businessCategory = profileInfo.businessCategory;
        } else {
            payload.firstName = profileInfo.firstName;
            payload.lastName = profileInfo.lastName;
        }

        return payload;
    };

    const onCreateUser = () => {
        const isValid = validateForm();

        if (!isValid) {
            return;
        }

        createUser(getNewProfilePayload());
    };

    useEffect(() => {
        if (isSuccess) {
            setProfileInfo(profileInfoInitial);
            setErrors({});
            setShowPassword(false);
        }
    }, [isSuccess]);

    const fields = [
        "email",
        "password",
        "mobile",
        ...(isBusiness
            ? ["businessName"]
            : ["firstName", "lastName"])
    ];

    return (
        <ListLayout>
            <PageCard>
                <ProfileInfoAnimate
                    variants={fadeVariants}
                    animate="in"
                    exit="out"
                    initial="out"
                >
                    <Flex align="center">
                        <CheckBox
                            active={isBusiness}
                            onClick={() =>
                                onInputValueChange(
                                    "isBusiness",
                                    !isBusiness
                                )
                            }
                        />

                        <Flex
                            style={{
                                marginLeft: "10px"
                            }}
                        >
                            <DText main>
                                <Text tid="business-account" />
                            </DText>
                        </Flex>
                    </Flex>

                    <Flex fw wrap>
                        {/* Country */}
                        {/* Country */}
                        <ProfileInfoItem>
                            <Select
                                isCountry
                                options={countryOptions}
                                value={profileInfo.countryCode}
                                onValueChange={(value) => {
                                    onInputValueChange(
                                        "countryCode",
                                        countryOptions[value]
                                    );
                                }}
                                placeHolder="select-country"
                                size="small"
                                fontSize="s"
                            />

                            {errors.countryCode && (
                                <DText
                                    style={{
                                        marginTop: "5px",
                                        fontSize: "12px",
                                        color: "#ef4444"
                                    }}
                                >
                                    {errors.countryCode}
                                </DText>
                            )}
                        </ProfileInfoItem>

                        {/* Business Category */}
                        {isBusiness && (
                            <ProfileInfoItem>
                                <Select
                                    options={businessCategories}
                                    value={profileInfo.businessCategory}
                                    onValueChange={(value) =>
                                        onInputValueChange(
                                            "businessCategory",
                                            businessCategories[value]
                                        )
                                    }
                                    placeHolder="select-business-category"
                                    size="small"
                                    fontSize="s"
                                />

                                {errors.businessCategory && (
                                    <DText
                                        style={{
                                            marginTop: "5px",
                                            fontSize: "12px",
                                            color: "#ef4444"
                                        }}
                                    >
                                        {errors.businessCategory}
                                    </DText>
                                )}
                            </ProfileInfoItem>
                        )}

                        {/* Inputs */}
                        {fields.map((key) => {
                            const value =
                                profileInfo[key];

                            const isPassword =
                                key === "password";

                            return (
                                <ProfileInfoItem
                                    key={key}
                                >
                                    <div
                                        style={{
                                            position:
                                                "relative"
                                        }}
                                    >
                                        <Input
                                            className="!m-0"
                                            value={value}
                                            label={key}
                                            type={
                                                isPassword
                                                    ? showPassword
                                                        ? "text"
                                                        : "password"
                                                    : "text"
                                            }
                                            onInputChange={(
                                                v
                                            ) =>
                                                onInputValueChange(
                                                    key,
                                                    v
                                                )
                                            }
                                            code={numberInputTypes.includes(
                                                key
                                            )}
                                            direction={
                                                key === "mobile"
                                                    ? "ltr"
                                                    : undefined
                                            }
                                            currency={
                                                key === "mobile"
                                                    ? mobilePrefix
                                                    : null
                                            }
                                            maxLength={
                                                key === "mobile"
                                                    ? 15
                                                    : isPassword
                                                        ? 128
                                                        : inputLength[
                                                        key
                                                        ]
                                            }
                                        />

                                        {isPassword && (
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    setShowPassword(
                                                        (
                                                            state
                                                        ) =>
                                                            !state
                                                    )
                                                }
                                                style={{
                                                    position:
                                                        "absolute",
                                                    left: "12px",
                                                    top: "50%",
                                                    transform:
                                                        "translateY(-50%)",
                                                    border: "none",
                                                    background:
                                                        "transparent",
                                                    cursor: "pointer",
                                                    padding:
                                                        "4px 8px",
                                                    fontSize:
                                                        "12px",
                                                    color: "#888",
                                                    zIndex: 2
                                                }}
                                            >
                                                {showPassword
                                                    ? "Hide"
                                                    : "Show"}
                                            </button>
                                        )}
                                    </div>

                                    {errors[key] && (
                                        <DText
                                            style={{
                                                marginTop:
                                                    "5px",
                                                fontSize:
                                                    "12px",
                                                color: "#ef4444"
                                            }}
                                        >
                                            {errors[key]}
                                        </DText>
                                    )}
                                </ProfileInfoItem>
                            );
                        })}
                    </Flex>

                    <Flex fw>
                        <Flex
                            width="280px"
                            style={{
                                margin: "30px 0"
                            }}
                        >
                            <ActionButton
                                active
                                onClick={onCreateUser}
                                loading={isLoading}
                            >
                                <Text tid="create-user" />
                            </ActionButton>
                        </Flex>
                    </Flex>
                </ProfileInfoAnimate>
            </PageCard>
        </ListLayout>
    );
};

export default AddUser;
